import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { FlagState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export const FlagDetails = memo(function FlagDetails({
  stepSelect,
  state,
  setState,
  flowType,
}: StepTypeDetailsProps<FlagState>) {
  const { t } = useTranslation();
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const nextStepChange = useHandler((next?: StepId) => {
    setState((state) => ({ ...state, next }));
  });
  const handleReason = useHandler((reason: string) => {
    setState((state) => ({ ...state, reason }));
  });

  return (
    <>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Reason")}
        </Text>
        <RedoTextareaInputField
          placeholder={t("Enter reason for flagging...")}
          readOnly={!canEditReturnsSettings}
          setValue={handleReason}
          value={state.reason}
        />
        <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
          {t("A description of why this return is being flagged.")}
        </Text>
      </Flex>
      <Divider />
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Next step")}
        </Text>
        {stepSelect({ value: state.next, valueChange: nextStepChange })}
        {flowType !== FlowType.FINALIZE_RETURN &&
          flowType !== FlowType.FINALIZE_CLAIM &&
          !state.next && (
            <Text fontSize="xs" textColor="error">
              {t("Required")}
            </Text>
          )}
      </Flex>
    </>
  );
});
